import type { FetchBaseQueryError } from "@reduxjs/toolkit/query";
import { HttpStatusCode } from "axios";
import { getApiErrorUserMessage, readApiError } from "_shared/_errors/api-error";
import { getApiErrorData, getApiErrorStatus } from "_shared/_errors/api-error-message";
import { api } from "_shared/_stores/api";
import Guid from "_shared/guids/guid";
import AcceptEntityUserInvitationAPIRequest from "legal-entities/invitations/member-invitations/email-based/_api/commands/accept-entity-user-invitation-api-command";
import CreateUserEntityMemberInvitationAPIRequest from "legal-entities/invitations/member-invitations/email-based/_api/commands/create-user-entity-member-invitation-api-command";
import UserEntityMemberInvitationSchema, {
  UserEntityMemberInvitationJSON,
} from "legal-entities/invitations/member-invitations/email-based/_schema/user-entity-member-invitation-schema";
import UserEntityMemberInvitation, {
  MemberInvitationNotOpenError,
  UserMemberInvitationNotFoundError,
} from "legal-entities/invitations/member-invitations/email-based/user-entity-member-invitation";
import { InvalidRegistrationInfoError } from "legal-entities/registration-info/registration-info";
import Session from "users/__session/session";

type UserEntityMemberInvitationIdArgs = {
  id: Guid;
};

type GetActiveUserEntityMemberInvitationsArgs = {
  entityId: Guid;
};

type CreateUserEntityMemberInvitationArgs = {
  accessTemplateId?: Guid;
  invitation: UserEntityMemberInvitation;
};

type CancelUserEntityMemberInvitationArgs = {
  invitation: UserEntityMemberInvitation;
};

type AcceptUserEntityMemberInvitationArgs = {
  invitation: UserEntityMemberInvitation;
  session: Session;
};

const jsonHeaders = {
  "Content-Type": "application/json",
};

export const userEntityMemberInvitationApi = api.injectEndpoints({
  endpoints: (build) => ({
    getActiveUserEntityMemberInvitations: build.query<
      UserEntityMemberInvitation[],
      GetActiveUserEntityMemberInvitationsArgs
    >({
      async queryFn({ entityId }, _queryApi, _extraOptions, baseQuery) {
        const response = await baseQuery(`legal-entities/${entityId.value}/user-entity-member-invitations`);

        if (response.error) {
          return {
            error: toQueryError(
              new UserEntityMemberInvitationAPIServiceError("getActiveUserEntityMemberInvitations", response.error),
            ),
          };
        }

        const invitations = (response.data as UserEntityMemberInvitationJSON[]).map((invitation) =>
          new UserEntityMemberInvitation(UserEntityMemberInvitationSchema.deserialize(invitation)),
        );
        return { data: invitations };
      },
      providesTags: (_result, _error, { entityId }) => [
        { type: "UserEntityMemberInvitation" as const, id: `LegalEntity-${entityId.value}` },
      ],
    }),
    getUserEntityMemberInvitationById: build.query<UserEntityMemberInvitation, UserEntityMemberInvitationIdArgs>({
      async queryFn({ id }, _queryApi, _extraOptions, baseQuery) {
        const response = await baseQuery(`legal-entities/user-entity-member-invitations/${id.value}`);

        if (response.error) {
          if (getApiErrorStatus(response.error) === HttpStatusCode.NotFound) {
            return { error: toQueryError(new UserMemberInvitationNotFoundError(id)) };
          }

          return {
            error: toQueryError(
              new UserEntityMemberInvitationAPIServiceError("getUserEntityMemberInvitationById", response.error),
            ),
          };
        }

        const params = UserEntityMemberInvitationSchema.deserialize(response.data as UserEntityMemberInvitationJSON);
        return { data: new UserEntityMemberInvitation(params) };
      },
    }),
    createUserEntityMemberInvitation: build.mutation<void, CreateUserEntityMemberInvitationArgs>({
      async queryFn({ invitation, accessTemplateId }, _queryApi, _extraOptions, baseQuery) {
        const response = await baseQuery({
          url: `legal-entities/${invitation.userEntityInfo.entityId.value}/user-entity-member-invitations`,
          method: "POST",
          body: JSON.stringify(new CreateUserEntityMemberInvitationAPIRequest(invitation, accessTemplateId)),
          headers: jsonHeaders,
        });

        if (response.error) {
          return {
            error: toQueryError(
              new UserEntityMemberInvitationAPIServiceError("createUserEntityMemberInvitation", response.error, invitation),
            ),
          };
        }

        return { data: null as unknown as void };
      },
      invalidatesTags: (_result, _error, { invitation }) => [
        { type: "UserEntityMemberInvitation" as const, id: `LegalEntity-${invitation.userEntityInfo.entityId.value}` },
      ],
    }),
    cancelUserEntityMemberInvitation: build.mutation<void, CancelUserEntityMemberInvitationArgs>({
      async queryFn({ invitation }, _queryApi, _extraOptions, baseQuery) {
        if (!invitation.id) {
          return { error: toQueryError(new Error("Cannot cancel an invitation that has not been saved.")) };
        }

        const response = await baseQuery({
          url: `legal-entities/${invitation.userEntityInfo.entityId.value}/user-entity-member-invitations/${invitation.id.value}/cancel`,
          method: "POST",
        });

        if (response.error) {
          if (getApiErrorStatus(response.error) === HttpStatusCode.NotFound) {
            return { error: toQueryError(new UserMemberInvitationNotFoundError(invitation.id)) };
          }
          if (getApiErrorStatus(response.error) === HttpStatusCode.Conflict) {
            return { error: toQueryError(new MemberInvitationNotOpenError(conflictMessage(response.error))) };
          }

          return {
            error: toQueryError(
              new UserEntityMemberInvitationAPIServiceError(
                "cancelUserEntityMemberInvitation",
                response.error,
                invitation,
              ),
            ),
          };
        }

        return { data: null as unknown as void };
      },
      invalidatesTags: (_result, _error, { invitation }) => [
        { type: "UserEntityMemberInvitation" as const, id: `LegalEntity-${invitation.userEntityInfo.entityId.value}` },
      ],
    }),
    acceptUserEntityMemberInvitation: build.mutation<void, AcceptUserEntityMemberInvitationArgs>({
      async queryFn({ invitation, session }, _queryApi, _extraOptions, baseQuery) {
        if (!invitation.id) {
          return { error: toQueryError(new Error("Invitation ID is required to accept an invitation.")) };
        }
        if (!invitation.registrationInfo?.password) {
          return { error: toQueryError(new Error("Password is required to accept an invitation.")) };
        }

        const response = await baseQuery({
          url: `legal-entities/user-entity-member-invitations/${invitation.id.value}/accept`,
          method: "POST",
          body: JSON.stringify(
            new AcceptEntityUserInvitationAPIRequest(
              invitation.registrationInfo.password,
              invitation.registrationInfo.name,
            ),
          ),
          headers: jsonHeaders,
        });

        if (response.error) {
          if (getApiErrorStatus(response.error) === HttpStatusCode.BadRequest) {
            return { error: toQueryError(new InvalidRegistrationInfoError(getApiErrorData(response.error))) };
          }
          if (getApiErrorStatus(response.error) === HttpStatusCode.NotFound) {
            return { error: toQueryError(new UserMemberInvitationNotFoundError(invitation.id)) };
          }
          if (getApiErrorStatus(response.error) === HttpStatusCode.Conflict) {
            return { error: toQueryError(new MemberInvitationNotOpenError(conflictMessage(response.error))) };
          }

          return {
            error: toQueryError(
              new UserEntityMemberInvitationAPIServiceError(
                "acceptUserEntityMemberInvitation",
                response.error,
                invitation,
              ),
            ),
          };
        }

        // Accepting a member invitation is a login context and returns the shared auth payload.
        session.initializeFromAuthPayload(response.data);
        return { data: null as unknown as void };
      },
    }),
  }),
});

export class UserEntityMemberInvitationAPIServiceError extends Error {
  invitation?: UserEntityMemberInvitation;
  method: string;
  error: unknown;

  constructor(method: string, error: unknown, invitation?: UserEntityMemberInvitation) {
    super(`UserEntityMemberInvitationAPIService: ${error instanceof Error ? error.message : String(error)}`);
    this.method = method;
    this.error = error;
    this.invitation = invitation;
  }
}

function toQueryError(error: Error): FetchBaseQueryError {
  return error as unknown as FetchBaseQueryError;
}

function conflictMessage(error: unknown): string | undefined {
  const result = readApiError(error);
  return result.trusted ? getApiErrorUserMessage(result) : undefined;
}

export const {
  useGetActiveUserEntityMemberInvitationsQuery,
  useGetUserEntityMemberInvitationByIdQuery,
  useLazyGetUserEntityMemberInvitationByIdQuery,
  useCreateUserEntityMemberInvitationMutation,
  useCancelUserEntityMemberInvitationMutation,
  useAcceptUserEntityMemberInvitationMutation,
} = userEntityMemberInvitationApi;
